import { format } from "date-fns";
import { Lock } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/confirm-dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { usePermissions } from "@/hooks/use-permissions.ts";
import { useSetStagesLock } from "../../hooks/use-set-stages-lock.ts";
import type { StagesLock as StagesLockState } from "../../types.ts";

function lockDescription(lock: StagesLockState): string {
	const parts = ["Estrutura travada"];
	if (lock.changedByName) parts.push(`por ${lock.changedByName}`);
	if (lock.changedAt) {
		parts.push(`em ${format(new Date(lock.changedAt), "dd/MM/yyyy HH:mm")}`);
	}
	return parts.join(" ");
}

export function StagesLock({
	constructionId,
	lock,
}: {
	constructionId: string;
	lock: StagesLockState;
}) {
	const { has } = usePermissions();
	const canLock = has("construction_stages:lock");
	const setLock = useSetStagesLock(constructionId);
	const [confirmOpen, setConfirmOpen] = useState(false);

	if (!lock.locked && !canLock) return null;

	function apply(locked: boolean) {
		setLock.mutate(locked, {
			onSuccess: () => {
				toast.success(
					locked ? "Estrutura travada com sucesso!" : "Estrutura destravada.",
				);
				setConfirmOpen(false);
			},
			onError: () => {
				toast.error("Houve um erro ao alterar a trava da estrutura.");
				setConfirmOpen(false);
			},
		});
	}

	return (
		<div className="stages-lock" data-locked={lock.locked}>
			{lock.locked ? (
				<p className="stages-lock__status">
					<Lock className="stages-lock__icon" aria-hidden="true" />
					{lockDescription(lock)}
				</p>
			) : (
				<p className="stages-lock__status">Estrutura liberada para edição.</p>
			)}
			{canLock && (
				<div className="stages-lock__toggle">
					<Switch
						id="stages-lock-toggle"
						checked={lock.locked}
						disabled={setLock.isPending}
						onCheckedChange={(checked) => {
							if (checked) {
								setConfirmOpen(true);
							} else {
								apply(false);
							}
						}}
					/>
					<Label htmlFor="stages-lock-toggle">Travar estrutura</Label>
				</div>
			)}
			{canLock && (
				<ConfirmDialog
					open={confirmOpen}
					onOpenChange={setConfirmOpen}
					title="Travar estrutura"
					description="Com a estrutura travada, ninguém poderá criar, editar, excluir, reordenar ou aplicar templates nas etapas desta obra. A troca de status e as medições continuam liberadas."
					confirmLabel="Travar"
					isPending={setLock.isPending}
					onConfirm={() => apply(true)}
				/>
			)}
		</div>
	);
}
